import {
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import React, { useEffect, useState } from "react";

import { PERMISSIONS } from "../../constants/roles";
import { REGION_RESTRICTION_LABELS } from "../../constants/voucher";
import { usePermissions } from "../App/PermissionContext";

import { SaveOutlined, EditOutlined } from "@ant-design/icons";
import { formatDateTime } from "../../helpers/date";

const { Text } = Typography;
const { TextArea } = Input;
const { TabPane } = Tabs;

const VoucherList = (props) => {
  const { permissions } = usePermissions();

  const [vouchers, setVouchers] = useState(props.vouchers);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [editForm] = Form.useForm();

  useEffect(() => {
    setVouchers(props.vouchers);
  }, [props.vouchers]);

  const toggleHandler = (id, isActive) => {
    props.onToggle(id, isActive);
  };

  const updateHandler = (id, data) => {
    props.onUpdate(id, data);
  };

  const openEditModal = (record) => {
    setSelectedVoucher(record);
    editForm.setFieldsValue({
      tags: record.tags || [],
      // imageUrl: record.imageUrl || "",
      reward_en: record.reward?.en || "",
      reward_zh: record.reward?.zh || "",
      reward_ms: record.reward?.ms || "",
      tnc_en: record.tnc?.en || "",
      tnc_zh: record.tnc?.zh || "",
      tnc_ms: record.tnc?.ms || "",
    });
    setEditModalVisible(true);
  };

  const handleEditSubmit = async (values) => {
    const payload = {
      tags: values.tags,
      // imageUrl: values.imageUrl,
      reward: {
        en: values.reward_en || "",
        zh: values.reward_zh || "",
        ms: values.reward_ms || "",
      },
      tnc: {
        en: values.tnc_en || "",
        zh: values.tnc_zh || "",
        ms: values.tnc_ms || "",
      },
    };
    await updateHandler(selectedVoucher.objectId, payload);
    setEditModalVisible(false);
    setSelectedVoucher(null);
    editForm.resetFields();
  };

  const handleEditCancel = () => {
    setEditModalVisible(false);
    setSelectedVoucher(null);
    editForm.resetFields();
  };

  const table = [
    {
      title: "Name",
      dataIndex: "name",
    },
    {
      title: "Category",
      dataIndex: "category",
      render: (text) => (text !== "order" ? "General" : "Order"),
    },
    {
      title: "Description",
      dataIndex: "description",
    },
    {
      title: "Tags",
      dataIndex: "tags",
      render: (tags) =>
        tags && tags.length > 0 ? (
          <Space wrap>
            {tags.map((tag, index) => (
              <Tag key={index} color="blue">
                {tag}
              </Tag>
            ))}
          </Space>
        ) : ("-")
    },
    // {
    //   title: "Image",
    //   dataIndex: "imageUrl",
    //   render: (url) =>
    //     url ? (
    //       <img
    //         src={url}
    //         alt="voucher"
    //         style={{width: 40, height: 40, objectFit: "cover"}}
    //       />
    //     ) : ("-")
    // },
    {
      title: "Minimum Order Value (RM)",
      dataIndex: "minSpend",
      render: (text) => (text ? `RM ${text.toFixed(2)}` : "No Minimum"),
    },
    {
      title: "Discount Value",
      dataIndex: "amount",
      render: (_, record) =>
        record.type === "amount" ? `RM ${record.amount}` : `${record.percent}%`,
    },
    {
      title: "Quantity per merchant",
      dataIndex: "uses",
      align: "center",
      render: (text) => text || 1,
    },
    {
      title: "Couriers",
      dataIndex: "serviceProviders",
      align: "center",
      render: (providers, record) => {
        if (record.category !== "order") return "-";

        if (providers && providers.length > 0)
          return (
            <Tooltip title={providers.join(", ")}>
              <a>{providers.length}</a>
            </Tooltip>
          );

        return "All";
      },
    },
    {
      title: "Max Weight",
      dataIndex: "maxWeightKg",
      align: "center",
      render: (maxWeightKg, record) => {
        if (record.category !== "order") return "-";

        return maxWeightKg !== undefined && maxWeightKg !== null
          ? `${maxWeightKg} kg`
          : "Any";
      },
    },
    {
      title: "Region",
      dataIndex: "regionRestriction",
      align: "center",
      render: (region, record) => {
        if (record.category !== "order") return "-";

        return region ? REGION_RESTRICTION_LABELS[region] || region : "Any";
      },
    },
    {
      title: "Valid Days",
      dataIndex: "validDays",
      render: (text, record) => {
        if (permissions?.includes(PERMISSIONS.UPDATE_VOUCHER))
          return (
            <InputNumber
              value={text}
              precision={0}
              onChange={(value) => {
                setVouchers(
                  vouchers.map((voucher) =>
                    voucher.objectId === record.objectId
                      ? { ...voucher, validDays: value }
                      : voucher
                  )
                );
              }}
            />
          );

        return text;
      },
    },
    {
      title: "Expiry Date",
      dataIndex: "expireAt",
      render: (text) => (text ? formatDateTime(text) : "No Expiry"),
    },
    {
      title: "Total Dispatched",
      dataIndex: "dispatchedCount",
      render: (text) => text,
    },
    {
      title: "Total Redeemed",
      dataIndex: "redeemLimit",
      align: "end",
      width: 200,
      render: (text, record) => {
        if (permissions?.includes(PERMISSIONS.UPDATE_VOUCHER))
          return (
            <Space align="center" style={{ justifyContent: "end" }}>
              <Text>
                {record.redeemLimitBy === "count"
                  ? `${record.redeemedCount}`
                  : `RM ${record.totalRedeemedAmount.toFixed(2)}`}
              </Text>
              <Text>/</Text>
              <InputNumber
                value={text}
                prefix={record.redeemLimitBy === "count" ? undefined : "RM"}
                precision={record.redeemLimitBy === "count" ? 0 : 2}
                onChange={(value) => {
                  setVouchers(
                    vouchers.map((voucher) =>
                      voucher.objectId === record.objectId
                        ? { ...voucher, redeemLimit: value }
                        : voucher
                    )
                  );
                }}
              />
            </Space>
          );

        return record.redeemLimitBy === "count"
          ? `${record.redeemedCount} / ${text}`
          : `RM ${record.totalRedeemedAmount.toFixed(2)} / RM ${text.toFixed(
            2
          )}`;
      },
    },
    {
      title: "By Pass Credit User",
      dataIndex: "isByPass",
      render: (value, record) =>
        permissions?.includes(PERMISSIONS.UPDATE_VOUCHER) ? (
          <Space>
            <Switch
              checked={value}
              onChange={(value) => {
                setVouchers(
                  vouchers.map((voucher) =>
                    voucher.objectId === record.objectId
                      ? { ...voucher, isByPass: value }
                      : voucher
                  )
                );
              }}
            />
          </Space>
        ) : value === true ? (
          "Active"
        ) : (
          "Inactive"
        ),
    },
    {
      title: "Active",
      dataIndex: "isActive",
      render: (value, record) =>
        permissions?.includes(PERMISSIONS.UPDATE_VOUCHER) ? (
          <Space>
            <Switch
              checked={value}
              onChange={(checked) => {
                toggleHandler(record.objectId, checked);
              }}
            />
          </Space>
        ) : value === true ? (
          "Active"
        ) : (
          "Inactive"
        ),
    },
    ...(permissions?.includes(PERMISSIONS.UPDATE_VOUCHER)
      ? [
        {
          title: "Actions",
          dataIndex: "action",
          fixed: "right",
          width: 120,
          render: (_, record) => (
            <Space>
              <Tooltip title="Edit Voucher Details">
                <Button
                  type="default"
                  icon={<EditOutlined/>}
                  onClick={() => openEditModal(record)}
                />
              </Tooltip>
              <Tooltip title="Update">
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={() =>
                    updateHandler(record.objectId, {
                      validDays: record.validDays,
                      redeemLimit: record.redeemLimit,
                      isByPass: record.isByPass
                    })
                  }
                />
              </Tooltip>
            </Space>
          ),
        },
      ]
      : []),
  ];

  return (
    <div className="table">
      <Table
        rowKey="objectId"
        className="table-section"
        columns={table}
        dataSource={vouchers}
        pagination={false}
        scroll={{ x: true }}
        loading={props.loading}
      />

      <Modal
        title="Edit Voucher Details"
        visible={editModalVisible}
        onCancel={handleEditCancel}
        footer={null}
        width={800}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleEditSubmit}
          autoComplete="off"
        >
          <Form.Item label="Tags" name="tags">
            <Select mode="tags" placeholder="Enter tags"/>
          </Form.Item>

          {/* <Form.Item label="Image URL" name="imageUrl">
            <Input placeholder="https://example.com/voucher-image.png" />
          </Form.Item>  */}

          <Tabs defaultActiveKey="en">
            <TabPane tab="English" key="en">
              <Form.Item label="Rewards Description" name="reward_en">
                <TextArea rows={2} placeholder="Enter reward description" />
              </Form.Item>
              <Form.Item label="Terms & Conditions" name="tnc_en">
                <TextArea rows={3} placeholder="Enter terms & conditions" />
              </Form.Item>
            </TabPane>
            <TabPane tab="Chinese" key="zh">
              <Form.Item label="Rewards Description" name="reward_zh">
                <TextArea rows={2} placeholder="Enter reward description" />
              </Form.Item>
              <Form.Item label="Terms & Conditions" name="tnc_zh">
                <TextArea rows={3} placeholder="Enter terms & conditions" />
              </Form.Item>
            </TabPane>
            <TabPane tab="Malay" key="ms">
              <Form.Item label="Rewards Description" name="reward_ms">
                <TextArea rows={2} placeholder="Enter reward description" />
              </Form.Item>
              <Form.Item label="Terms & Conditions" name="tnc_ms">
                <TextArea rows={3} placeholder="Enter terms & conditions" />
              </Form.Item>
            </TabPane>
          </Tabs>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">Save Changes</Button>
              <Button
                onClick={handleEditCancel}
              >Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default VoucherList;
